<script setup>
import {computed, ref, onMounted, onBeforeUnmount, watch} from 'vue'
import {useStore} from 'vuex'
import {useRoute, useRouter} from 'vue-router'
import { toTypedSchema } from '@vee-validate/zod'
import * as z from 'zod'
import {Spinner} from '@/components/ui/spinner'
import { v4 as uuidv4 } from 'uuid'
import { DatePicker } from 'v-calendar'
import { getResources, deleteResource } from '@/services/resourcesApi'

import { MediaUpload, MediaPreview, MediaDialog } from '@/components/media'

import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover'
import {Badge} from '@/components/ui/badge'
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form'
import { Input } from '@/components/ui/input'
import { Switch } from '@/components/ui/switch'
import { Button } from '@/components/ui/button'
import { toast } from '@/components/ui/toast'
import {Select, SelectContent, SelectGroup, SelectItem, SelectTrigger, SelectValue} from "@/components/ui/select"
import { ProjectSelect } from '@/components/ui/project-select'
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group'
import Label from "@/components/ui/label/Label.vue";
import { Textarea } from '@/components/ui/textarea'
import ColorPicker from '@/components/ui/ColorPicker.vue'
import FeatureSelection from '@/components/feature-selection/FeatureSelection.vue'

const formUUID = uuidv4()

const uploadedResources = ref([])
const showMediaDialog = ref(false)
const selectedMediaIndex = ref(0)
const mediaUploadRef = ref(null)

const props = defineProps({
  feature: {
    type: Object,
    required: true,
  },
  templateId: {
    type: String,
    default: null,
  },
  templateProjectId: {
    type: String,
    default: null,
  },
  predefinedProjectId: {
    type: String,
    default: null,
  },
  editMode: {
    type: Boolean,
    default: false,
  },
  predefined: {
    type: Boolean,
    default: false,
  },
  hideClose: {
    type: Boolean,
    default: false,
  },
})

const route = useRoute()
const router = useRouter()

const store = useStore()
const map = computed(() => store.getters['geo/getMap'])
const isMapReady = computed(() => store.getters['geo/getIsMapReady'])
const member = computed(() => store.getters['user/getMember'])
const lastSelectedProject = computed(() => store.getters['project/getLastSelected'])
const projects = computed(() => store.getters['project/getFlatedProjects'])
const selectedOrg = computed(() => store.getters['org/getSelectedOrg'])
const allTemplates = computed(() => store.getters['geo/getAllTemplates'] || [])
const templates = computed(() => {
  if (!allTemplates.value || allTemplates.value.length === 0) {
    return []
  }

  // Get the geometry type from the feature
  const geometryType = geojson.value?.geometry?.type
  if (!geometryType) {
    return []
  }

  // Filter templates by geometry type
  let filtered = allTemplates.value.filter(t => {
    // Check if template geometry type matches
    return t.geometry_type === geometryType
  })

  // If a project is selected, optionally filter by project
  const projectId = selectedProjectId.value || initialProjectId.value
  if (projectId && filtered.length > 0) {
    // You can choose to show all templates or only project-specific ones
    // For now, let's show all templates but sort project-specific ones first
    filtered = filtered.sort((a, b) => {
      const aIsProjectTemplate = a.project_id === projectId
      const bIsProjectTemplate = b.project_id === projectId
      if (aIsProjectTemplate && !bIsProjectTemplate) return -1
      if (!aIsProjectTemplate && bIsProjectTemplate) return 1
      return 0
    })
  }

  return filtered
})
const draw = computed(() => store.getters['geo/getDraw'])

const projectId = computed(() => parseInt(route.params.project_id))
const currentProject = computed(() => {
  if (!projectId.value) return null
  return projects.value.find(p => p.project_id === projectId.value)
})
const isSubmiting = ref(false)
const geojson = ref(null)
const selectedProjectId = ref(null)

geojson.value = props.feature
geojson.value.properties.is_static = geojson.value.properties.is_static || false

// Helper function to get property with default fallback
const getPropertyDefault = (propertyName, fallback = false) => {
  return props.feature && geojson.value.properties?.[propertyName] !== undefined
    ? geojson.value.properties[propertyName]
    : fallback
}

// Helper function to convert geofence events array to string
const convertGeofenceEventsToString = (events) => {
  if (!events || !Array.isArray(events)) return null
  if (events.includes('in') && events.includes('out')) return 'both'
  if (events.includes('in')) return 'in'
  if (events.includes('out')) return 'out'
  return null
}

const isAllowChangeCreatedDate = computed(() => geojson.value.properties.created_origin)

const emit = defineEmits(['update:created', 'update:close', 'update:saved', 'update:feature'])

const selectedGeometry = ref(null)

// Initialize selectedGeometry from the feature if it has geometry
if (props.feature?.geometry) {
  selectedGeometry.value = {
    type: 'Feature',
    id: props.feature.properties?.code || props.feature.properties?.local_uuid || 'initial',
    geometry: props.feature.geometry,
    properties: {
      name: props.feature.properties?.name || 'Current location',
      isDrawn: true,
    }
  }
}

const handleLocationSelect = (feature) => {
  if (!feature || !feature.geometry) return
  geojson.value.geometry = feature.geometry
  emit('update:feature', geojson.value)
}

// Determine which project to use - template's project takes precedence
// Make it computed to be reactive to route changes
const initialProjectId = computed(() => {
  if (props.templateProjectId) return parseInt(props.templateProjectId)
  if (props.predefinedProjectId) return parseInt(props.predefinedProjectId)
  return projectId.value
})

// Allow project_id to be nullable when no project is selected
// Use string initially for Select component, convert to number in onSubmit
const projectIdSchema = initialProjectId.value ?
  z.string().default(initialProjectId.value.toString()) :
  (projectId.value ? z.string().default(projectId.value.toString()) : z.string().nullable())

const schema = ref(z.object({
  color: z.string().default(getPropertyDefault('color', '#0059ff')),
  project_id: projectIdSchema,
  template_id: z.string().nullable().optional().default(props.templateId ? props.templateId : null),
  name: z.string().min(2).max(255).default(getPropertyDefault('name', '')),
  description: z.string().max(255).optional().default(getPropertyDefault('description', '')),
  shared: z.boolean().default(getPropertyDefault('shared', false)),
  is_static: z.boolean().default(getPropertyDefault('is_static', false)),
  geofence: z.boolean().default(getPropertyDefault('geofence', false)),
  geofenceEvents: z.string().default(convertGeofenceEventsToString(geojson.value.properties?.geofence_events)).optional(),
  geofencePrompt: z.boolean().default(getPropertyDefault('geofence_prompt', false)),
  created_origin: z.date().default(geojson.value.properties.created_origin ? new Date(geojson.value.properties.created_origin) : new Date(Date.now())),
}))

const onSubmit = async (values) => {

  // Convert project_id to number
  const projectId = typeof values.project_id === 'string' ? parseInt(values.project_id) : values.project_id

  // Validate that project_id is set
  if (!projectId) {
    toast({
      title: 'Please select a project',
      description: 'A project must be selected before creating a feature',
      variant: 'destructive'
    })
    isSubmiting.value = false
    return
  }

  isSubmiting.value = true

  try {
    if (!props.editMode) {
      // Get template name if template is selected
      let template_name = null
      if (values.template_id) {
        const template = templates.value.find(t => t.id === parseInt(values.template_id))
        if (template) {
          template_name = template.name
        }
      }

      const feature = await store.dispatch('geo/saveFeature', {
        project_id: projectId,
        files: null,
        feature: {
          type: 'Feature',
          properties: {
            name: values.name,
            description: values.description,
            shared: values.shared,
            is_static: values.is_static,
            member_id: member.value.member_id,
            created_origin: values.created_origin.getTime(),
            type: geojson.value.geometry?.type,
            subtype: 'report', // deprecated, need to remove
            geofence: values.geofence,
            geofence_events: values.geofenceEvents === 'both' ? ['in', 'out'] : [values.geofenceEvents],
            geofence_prompt: values.geofencePrompt,
            template_id: parseInt(values.template_id),
            template_name: template_name,
            color: values.color,
            project_id: projectId,

            data: values.dataFields ? Object.entries(values.dataFields).map(([key, value]) => {
              return {
                key,
                value,
                type: typeof value === 'number' ? 1 : typeof value === 'boolean' ? 2 : 0
              }
            }) : []
            // Don't include resources during creation - any queued resources will be uploaded in a deferred step after feature creation (see lines 227-238)
          },
          geometry: geojson.value.geometry
        }
      })

      // Upload deferred resources if any
      if (mediaUploadRef.value && feature?.properties?.id) {
        const queuedFiles = mediaUploadRef.value.getQueuedFiles()
        if (queuedFiles && queuedFiles.length > 0) {
          toast({
            title: 'Uploading resources',
            description: `Uploading ${queuedFiles.length} file(s)...`
          })

          // Start upload with the new feature code
          await mediaUploadRef.value.startUpload(feature.properties.code)
        }
      }

      geojson.value.properties.code = feature.properties.code
      geojson.value.properties.project_id = feature.properties.project_id
      geojson.value.properties.id = feature.properties.id
      emit('update:created', geojson.value)
      redirectToFeature(feature)
    } else {
      const feature = await store.dispatch('geo/updateFeature', {
        project_id: projectId,
        files: null,
        feature: {
          type: 'Feature',
          properties: {
            ...geojson.value.properties,
            name: values.name,
            description: values.description,
            shared: values.shared,
            is_static: values.is_static,
            member_id: member.value.member_id,
            created_origin: values.created_origin.getTime(),
            type: geojson.value.geometry?.type,
            subtype: 'report', // deprecated, need to remove
            geofence: values.geofence,
            geofence_events: values.geofenceEvents === 'both' ? ['in', 'out'] : [values.geofenceEvents],
            geofence_prompt: values.geofencePrompt,
            template_id: parseInt(values.template_id),
            color: values.color,
            project_id: projectId,

            data: values.dataFields ? Object.entries(values.dataFields).map(([key, value]) => {
              return {
                key,
                value,
                type: typeof value === 'number' ? 1 : typeof value === 'boolean' ? 2 : 0
              }
            }) : [],
            resources: uploadedResources.value.map(r => r.uuid)
          },
          geometry: geojson.value.geometry
        },
      })

      emit('update:saved', geojson.value)
      redirectToFeature(feature)
    }

    toast({
      title: `Geometry ${values.name} saved`,
    })
  } catch (error) {
    toast({
      title: 'Error saving geometry',
      description: error.message || 'An unexpected error occurred',
      variant: 'destructive'
    })
  } finally {
    isSubmiting.value = false
  }
}

const handleProjectChange = (id) => {
  selectedProjectId.value = id
  // Fetch all organization templates if not already loaded
  if (selectedOrg.value && (!allTemplates.value || allTemplates.value.length === 0)) {
    store.dispatch('geo/fetchAllTemplates', selectedOrg.value.organization_id)
  }
}

const selectedTemplateId = ref(null)

const handleTemplateSelect = (id, passedProjectId = null) => {
  const template = templates.value.find(t => t.id === parseInt(id))
  if (!template?.data_fields) return

  // Sort fields by index (nulls last), then by id
  const sortedFields = [...template.data_fields].sort((a, b) => {
    if (a.index === null && b.index === null) return a.id - b.id
    if (a.index === null) return 1
    if (b.index === null) return -1
    return a.index - b.index
  })

  const dataFields = sortedFields.reduce((acc, field) => {
    switch (field.type) {
      case 0:
        acc[field.key] = z.string().describe({ type: field.type, index: field.index})
        break
      case 1:
        acc[field.key] = z.number().describe({ type: field.type, index: field.index})
        break
      case 2:
        acc[field.key] = z.boolean().default(false).describe({ type: field.type, index: field.index})
        break
      case 3:
        acc[field.key] = z.string().describe({ type: field.type, index: field.index})
        break
      case 4:
        acc[field.key] = z.enum(field.options).describe({ type: field.type, index: field.index})
        break
    }
    // acc[field.key] = templateTypeToSchema[field.type]
    return acc
  }, {})

  // Use passed project ID if provided, otherwise use initialProjectId or selectedProjectId
  const projectIdToUse = passedProjectId || selectedProjectId.value || initialProjectId.value
  const projectIdStr = projectIdToUse ? projectIdToUse.toString() : null

  schema.value = schema.value.extend({
    dataFields: z.object(dataFields),
    name: z.string().min(3).max(255).default(template.name),
    template_id: z.string().optional().default(id),
    project_id: z.string().default(projectIdStr),
    color: z.string().default(template.color),
    shared: z.boolean().default(template.shared),
    is_static: z.boolean().default(template.is_static || false)
  })

  selectedTemplateId.value = id

  // Update the feature color and emit the change
  if (template.color) {
    geojson.value.properties.color = template.color
    emit('update:feature', geojson.value)
  }
}

const close = () => {
  emit('update:close')
}

const changeFeature = (values) => {
  geojson.value = {
    ...geojson.value,
    properties: {
      ...geojson.value.properties,
      ...values,
    }
  }
  emit('update:feature', geojson.value)
}

const submitButton = ref(null)
watch(() => props.feature?.properties?.isUpload, (value) => {
  if (value) {
    submitButton.value.click()
  }
})

// Helper function to resolve organization ID with fallback chain
const resolveOrgId = (feature) => {
  return feature.properties.organization_id ||
         selectedOrg.value?.id ||
         selectedOrg.value?.organization_id ||
         route.params?.org_id ||
         route.params?.organization_id;
}

const redirectToFeature = (feature) => {
  const orgId = resolveOrgId(feature);

  if (!orgId) {
    return
  }

  router.push({
    name: 'Feature',
    params: {
      org_id: orgId,
      featureCode: feature.properties.code,
      project_id: feature.properties.project_id
    }
  })
}

const goToFeature = (feature) => {
  const orgId = resolveOrgId(feature);

  if (!orgId) {
    return
  }

  router.push({
    name: 'Feature',
    params: {
      org_id: orgId,
      featureCode: feature.properties.code,
      project_id: feature.properties.project_id
    }
  })
}

// Watch for project changes from route
watch(projectId, (newProjectId) => {
  if (newProjectId && !props.templateProjectId && !props.predefinedProjectId) {
    // Only update if we're not using a template or predefined project
    selectedProjectId.value = newProjectId
    handleProjectChange(newProjectId)

    // Update schema with new project
    schema.value = schema.value.extend({
      project_id: z.string().default(newProjectId.toString())
    })
  }
})

// Watch for predefinedProjectId changes
watch(() => props.predefinedProjectId, (newProjectId) => {
  if (newProjectId) {
    const projectIdInt = parseInt(newProjectId)
    selectedProjectId.value = projectIdInt
    handleProjectChange(projectIdInt)

    // Update schema with new project
    schema.value = schema.value.extend({
      project_id: z.string().default(newProjectId)
    })
  }
}, { immediate: true })

// Watch for organization changes to fetch templates
watch(selectedOrg, (newOrg) => {
  if (newOrg && newOrg.organization_id) {
    store.dispatch('geo/fetchAllTemplates', newOrg.organization_id)
  }
})

const loadExistingResources = async (featureId) => {
  try {
    const response = await getResources(featureId)
    if (response && response.resources) {
      uploadedResources.value = response.resources
    }
  } catch (error) {
  }
}

const handleResourceUploaded = (resource) => {
  uploadedResources.value.push(resource)
  toast({
    title: 'Resource uploaded',
    description: `${resource.filename} has been uploaded successfully`
  })
}

const handleResourceRemove = (resource) => {
  const index = uploadedResources.value.findIndex(r => r.id === resource.id)
  if (index !== -1) {
    uploadedResources.value.splice(index, 1)
  }
}

const handleMediaPreviewClick = (resource) => {
  const index = uploadedResources.value.findIndex(r => r.id === resource.id)
  if (index !== -1) {
    selectedMediaIndex.value = index
    showMediaDialog.value = true
  }
}

const handleMediaDelete = async (resource) => {
  try {
    await deleteResource(resource.uuid)
    handleResourceRemove(resource)
    toast({
      title: 'Resource deleted',
      description: `${resource.filename} has been deleted`
    })
  } catch (error) {
    toast({
      title: 'Delete failed',
      description: error.message,
      variant: 'destructive'
    })
  }
}

onMounted(() => {
  map.value.on('draw.update', () => {
    const data = draw.value.getAll()
    const feature = data.features.find(f =>
      (f.properties.code && f.properties.code === props.feature.properties.code) ||
      (f.properties.local_uuid && f.properties.local_uuid === props.feature.properties.local_uuid) ||
      (props.feature.properties._drawId && f.id === props.feature.properties._drawId)
    )
    if (feature) {
      geojson.value.geometry = feature.geometry
      emit('update:feature', geojson.value)
    }
  })

  // Fetch all organization templates on mount
  if (selectedOrg.value) {
    store.dispatch('geo/fetchAllTemplates', selectedOrg.value.organization_id)
  }

  // If template has a project, use that; otherwise use the current project from route
  const projectToUse = initialProjectId.value || projectId.value

  if (projectToUse) {
    handleProjectChange(projectToUse)

    if (props.templateId) {
      // Wait a bit for templates to load before selecting
      setTimeout(() => {
        handleTemplateSelect(props.templateId, projectToUse)
      }, 100)
    }
  } else if (projectId.value) {
    // If we have a project from the route but not from props, still load templates
    handleProjectChange(projectId.value)
  }

  // Auto-queue image resource file into MediaUpload
  if (props.feature.properties.resource && mediaUploadRef.value) {
    mediaUploadRef.value.addFiles([props.feature.properties.resource])
  }

  // Load existing resources if in edit mode
  if (props.editMode && geojson.value?.properties?.id) {
    loadExistingResources(geojson.value.properties.id)
  }
})

onBeforeUnmount(() => {
  store.commit('geo/SET_REPORT_TEMPLATES', null)
})
</script>

<template>
<Form
  v-slot="{ getValues, errors, validate }"
  class="space-y-3"
  @submit="onSubmit"
  @invalid-submit="(errors) => {
  }"
  :validation-schema="toTypedSchema(schema)"
  :key="`${selectedTemplateId}-${selectedProjectId || projectId || initialProjectId.value}`"
  role="form"
  :aria-label="editMode ? 'Edit feature form' : 'Create new feature form'"
  :aria-describedby="'form-description'"
  :data-testid="'feature-form'"
>
  <div class="sr-only" id="form-description">
    {{ editMode ? 'Form to edit an existing feature' : 'Form to create a new geographic feature' }}
  </div>

  <!-- Project Selection when no project is open -->
  <FormField v-if="!initialProjectId && !projectId" v-slot="{ componentField }" name="project_id">
    <FormItem>
      <FormLabel>Project</FormLabel>
      <FormControl>
        <ProjectSelect
          :projects="projects"
          :model-value="componentField.modelValue"
          placeholder="Select a project"
          @update:model-value="(val) => {
            componentField.onInput(val)
            handleProjectChange(parseInt(val))
            selectedProjectId = parseInt(val)
          }"
        />
      </FormControl>
      <FormMessage />
    </FormItem>
  </FormField>

  <!-- Current Project Display -->
  <div v-else-if="initialProjectId || projectId" class="mb-4">
    <div class="flex items-center">
      <div>
        <h3 class="text-sm font-medium">
          Project
        </h3>
        <div class="text-sm text-gray-500">
          <span class="font-semibold">{{ projects.find(p => p.project_id === (initialProjectId || projectId))?.project_name || 'Unknown Project' }}</span>
        </div>
      </div>
    </div>
  </div>

  <div class="space-y-1">
    <h3 class="text-sm font-medium">Location</h3>
    <FeatureSelection
      :selected-geometry="selectedGeometry"
      :allowed-draw-modes="['point', 'line', 'polygon']"
      @update:selected-geometry="selectedGeometry = $event"
      @select="handleLocationSelect"
    />
  </div>

  <FormField v-slot="{ componentField }" name="created_origin">
    <FormItem>
      <FormLabel>Created</FormLabel>
      <FormControl>
        <div :class="{'pointer-events-none opacity-25': !isAllowChangeCreatedDate}">
          <Popover>
            <PopoverTrigger>
              <Badge variant="secondary">
                {{ new Date(componentField.modelValue).toLocaleString() }}
              </Badge>
            </PopoverTrigger>
            <PopoverContent class="p-0 w-full">
              <DatePicker v-bind="componentField" mode="dateTime" is24hr />
            </PopoverContent>
          </Popover>
        </div>
      </FormControl>
      <FormMessage />
    </FormItem>
  </FormField>

  <FormField v-if="!props.templateId && templates && templates.length > 0" v-slot="{ componentField }" name="template_id">
    <FormItem>
      <FormLabel>Template</FormLabel>
      <FormControl>
        <Select v-bind="componentField" @update:model-value="(val) => handleTemplateSelect(val, selectedProjectId || initialProjectId.value || projectId)">
          <SelectTrigger data-testid="feature-template-select">
            <SelectValue placeholder="Select a type" />
          </SelectTrigger>
          <SelectContent>
            <SelectGroup>
              <SelectItem
                v-for="(t, index) of templates"
                :key="index"
                :value="t.id.toString()"
                :data-testid="`template-option-${t.id}`"
              >
                {{t.name}} ({{t.project_name || 'Organization'}})
              </SelectItem>
            </SelectGroup>
          </SelectContent>
        </Select>
      </FormControl>
      <FormMessage />
    </FormItem>
  </FormField>

  <FormField v-else-if="props.templateId && templates?.length" v-slot="{ value, componentField }" name="template_id">
    <FormItem>
      <FormLabel>Template</FormLabel>
      <FormControl>
        <span class="text-gray-500">
          {{ templates?.find(t => t.id === parseInt(props.templateId))?.name }}
        </span>
      </FormControl>
      <FormMessage />
    </FormItem>
  </FormField>

  <div class="flex space-x-3">
    <div class="basis-5/6">
      <FormField v-slot="{ componentField }" name="name">
        <FormItem>
          <FormLabel>Name</FormLabel>
          <FormControl>
            <Input
              v-bind="componentField"
              :disabled="selectedTemplateId ? true : false"
              :aria-label="'Feature name'"
              :aria-describedby="'name-description'"
              :data-testid="'feature-name-input'"
              placeholder="Enter feature name"
            />
          </FormControl>
          <FormMessage />
        </FormItem>
      </FormField>
    </div>
    <div class="basis-1/6">
      <FormField v-slot="{ componentField, value }" name="color">
        <FormItem>
          <FormLabel>Color</FormLabel>
          <FormControl>
            <div class="pt-1">
              <ColorPicker
                :model-value="componentField.modelValue"
                @update:model-value="(newColor) => {
                  componentField.onInput(newColor)
                  changeFeature(getValues())
                }"
                button-class="w-10 h-10 rounded-md border-2 border-gray-300 hover:border-gray-400 shadow-sm hover:shadow-md transition-all"
              />
            </div>
          </FormControl>
          <FormMessage />
        </FormItem>
      </FormField>
    </div>
  </div>

  <FormField v-slot="{ componentField }" name="description">
    <FormItem>
      <FormLabel>Description</FormLabel>
      <FormControl>
        <Input
          v-bind="componentField"
          :aria-label="'Feature description'"
          :data-testid="'feature-description-input'"
          placeholder="Enter feature description (optional)"
        />
      </FormControl>
      <FormMessage />
    </FormItem>
  </FormField>

  <div v-if="selectedTemplateId && templates?.length && schema.shape?.dataFields" class="space-y-3 pb-4">
    <h4 class="font-bold">Collected data</h4>

    <FormField
      v-for="(key, index) in Object.entries(schema.shape.dataFields.shape).sort((a, b) => {
        const indexA = a[1].description?.index
        const indexB = b[1].description?.index
        if (indexA === undefined && indexB === undefined) return 0
        if (indexA === undefined) return 1
        if (indexB === undefined) return -1
        return indexA - indexB
      })"
      v-slot="{ componentField, value, handleChange }"
      :key="index" :name="'dataFields.' + key[0]"
    >
      <FormItem>
        <FormLabel v-if="key[1].description.type !== 2">{{ key[0] }}</FormLabel>
        <FormControl>
          <Input v-if="key[1].description.type === 0" v-bind="componentField" type="text" />
          <Input v-if="key[1].description.type === 1" v-bind="componentField" type="number" />
          <div v-if="key[1].description.type === 2" class="flex items-center">
            <Switch
              :model-value="value"
              @update:model-value="handleChange"
            />
            <Label class="ml-2">{{ key[0] }}</Label>
          </div>
          <Textarea v-if="key[1].description.type === 3" v-bind="componentField" />
          <RadioGroup v-if="key[1].description.type === 4" v-bind="componentField">
            <FormItem v-for="value in key[1]._def.values" class="flex items-center space-y-0 gap-x-3">
              <FormControl>
                <RadioGroupItem :value="value" />
              </FormControl>
              <FormLabel class="font-normal">{{ value }}</FormLabel>
            </FormItem>
          </RadioGroup>
        </FormControl>
        <FormMessage />
      </FormItem>
    </FormField>
  </div>

  <!-- <FormField v-slot="{ value, handleChange }" name="is_static">
    <FormItem>
      <FormControl>
        <div class="flex items-center">
          <Switch
            :model-value="value"
            @update:model-value="handleChange"
          />
          <Label class="ml-2">Static</Label>
        </div>
      </FormControl>
      <FormMessage />
    </FormItem>
  </FormField> -->

  <FormField v-slot="{ value, handleChange }" name="shared">
    <FormItem>
      <FormControl>
        <div class="flex items-center">
          <Switch
            :model-value="value"
            @update:model-value="handleChange"
            :aria-label="'Make feature public'"
            :aria-describedby="'public-description'"
            :data-testid="'feature-public-switch'"
          />
          <Label class="ml-2 cursor-pointer" for="shared">Public</Label>
          <span class="sr-only" id="public-description">
            When enabled, this feature will be visible to other users
          </span>
        </div>
      </FormControl>
      <FormMessage />
    </FormItem>
  </FormField>

  <FormField
    v-slot="{ value, handleChange }"
    name="geofence"
    v-if="geojson.geometry?.type === 'Polygon' || geojson.geometry?.type === 'MultiPolygon'"
  >
    <FormItem>
      <FormControl>
        <div class="flex items-center">
          <Switch
            :model-value="value"
            @update:model-value="handleChange"
          />
          <Label class="ml-2">Virtual Geofence</Label>
        </div>
      </FormControl>
      <FormMessage />
    </FormItem>
  </FormField>

  <div v-if="getValues().geofence" class="p-2 space-y-3 border rounded border-gray-200">
    <FormField
      v-slot="{ componentField }"
      name="geofenceEvents"
    >
      <FormItem>
        <FormControl>
          <RadioGroup v-bind="componentField" class="flex space-x-3">
            <FormItem class="flex items-center space-y-0 gap-x-3">
              <FormControl>
                <RadioGroupItem value="in" />
              </FormControl>
              <FormLabel class="font-normal">In</FormLabel>
            </FormItem>
            <FormItem class="flex items-center space-y-0 gap-x-3">
              <FormControl>
                <RadioGroupItem value="out" />
              </FormControl>
              <FormLabel class="font-normal">Out</FormLabel>
            </FormItem>
            <FormItem class="flex items-center space-y-0 gap-x-3">
              <FormControl>
                <RadioGroupItem value="both" />
              </FormControl>
              <FormLabel class="font-normal">Both</FormLabel>
            </FormItem>
          </RadioGroup>
        </FormControl>
      </FormItem>
      <FormMessage />
    </FormField>

    <FormField
      v-slot="{ value, handleChange }"
      name="geofencePrompt"
      v-if="getValues().geofence && (geojson.geometry?.type === 'Polygon' || geojson.geometry?.type === 'MultiPolygon')"
    >
      <FormItem>
        <FormControl>
          <div class="flex items-center">
            <Switch
              :model-value="value"
              @update:model-value="handleChange"
            />
            <Label class="ml-2">Prompt user on crossing</Label>
          </div>
        </FormControl>
        <FormMessage />
      </FormItem>
    </FormField>
  </div>

  <div class="space-y-3">
    <h3 class="text-sm font-medium">Upload files</h3>
    <MediaUpload
      ref="mediaUploadRef"
      :feature-code="editMode && geojson?.properties?.code ? geojson.properties.code : null"
      :allowed-types="['image', 'video', 'audio']"
      :max-files="20"
      :defer-upload="!editMode"
      @uploaded="handleResourceUploaded"
      @remove="handleResourceRemove"
    />
  </div>

  <MediaDialog
    :open="showMediaDialog"
    :resources="uploadedResources"
    :initial-index="selectedMediaIndex"
    @update:open="showMediaDialog = $event"
    @delete="handleMediaDelete"
  />

  <div class="flex items-center justify-between">

    <div class="flex justify-end w-full">
      <Button
        type="submit"
        :disabled="isSubmiting"
        ref="submitButton"
        class="h-9 px-4 py-2"
        :aria-label="editMode ? 'Save feature changes' : 'Upload new feature'"
        :aria-describedby="editMode ? 'save-description' : 'upload-description'"
        :data-testid="editMode ? 'save-feature-button' : 'upload-feature-button'"
      >
        <Spinner v-if="isSubmiting" class="w-4 h-4 mr-2" aria-hidden="true" />
        <span v-if="!editMode">Upload</span>
        <span v-else>Save</span>
        <span class="sr-only" :id="editMode ? 'save-description' : 'upload-description'">
          {{ editMode ? 'Save changes to existing feature' : 'Create new feature with provided information' }}
        </span>
      </Button>
    </div>
  </div>
</Form>
</template>
